import { commandPaletteShortcuts } from "@redotech/redo-web/keyboard-shortcuts/command-palette-shortcuts";
import type { KeyboardShortcut } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import {
  messageInputShortcuts,
  priorityShortcuts,
  snoozeShortcuts,
  supportTicketActionShortcuts,
  supportTicketDetailShortcuts,
  tableNavigationShortcuts,
} from "@redotech/redo-web/keyboard-shortcuts/shopper-ai-shortcuts";

/**
 * One shortcut row in the modal. `shortcuts` holds every key combo that
 * triggers the same action so alternates (e.g. ↑ and ⌥J) render on one row.
 */
export interface ShortcutEntry {
  description: string;
  shortcuts: KeyboardShortcut[];
}

/** A section of shortcut rows shown under one header. */
export interface ShortcutCategory {
  key: string;
  label: string;
  entries: ShortcutEntry[];
}

/**
 * Every keyboard shortcut category, in display order. Key combos are referenced
 * from the shared registries so the displayed keys always match what the app
 * actually binds; only the human-readable descriptions live here.
 */
export const SHORTCUT_CATEGORIES: ShortcutCategory[] = [
  {
    key: "navigation",
    label: "Navigation",
    entries: [
      {
        description: "Next ticket in list",
        shortcuts: [tableNavigationShortcuts.NAVIGATE_DOWN],
      },
      {
        description: "Previous ticket in list",
        shortcuts: [tableNavigationShortcuts.NAVIGATE_UP],
      },
      {
        description: "Open selected ticket",
        shortcuts: [tableNavigationShortcuts.OPEN_SUPPORT_TICKET],
      },
      {
        description: "Open action menu",
        shortcuts: [
          tableNavigationShortcuts.OPEN_ACTION_MENU,
          supportTicketDetailShortcuts.OPEN_ACTION_MENU,
        ],
      },
      {
        description: "Next ticket while viewing",
        shortcuts: [
          supportTicketDetailShortcuts.NAVIGATE_DOWN,
          supportTicketDetailShortcuts.NAVIGATE_NEXT,
        ],
      },
      {
        description: "Previous ticket while viewing",
        shortcuts: [
          supportTicketDetailShortcuts.NAVIGATE_UP,
          supportTicketDetailShortcuts.NAVIGATE_PREV,
        ],
      },
    ],
  },
  {
    key: "compose",
    label: "Compose message",
    entries: [
      { description: "Send", shortcuts: [messageInputShortcuts.SEND_MESSAGE] },
      {
        description: "Send and close",
        shortcuts: [messageInputShortcuts.SEND_AND_CLOSE],
      },
      {
        description: "Send and mark as in progress",
        shortcuts: [messageInputShortcuts.SEND_AND_MARK_IN_PROGRESS],
      },
      {
        description: "Send and snooze",
        shortcuts: [messageInputShortcuts.SEND_AND_SNOOZE],
      },
      {
        description: "AI suggest a reply",
        shortcuts: [messageInputShortcuts.AI_SUGGEST],
      },
      {
        description: "AI polish draft",
        shortcuts: [messageInputShortcuts.AI_POLISH],
      },
      {
        description: "Mention a teammate",
        shortcuts: [messageInputShortcuts.INSERT_MENTION],
      },
      {
        description: "Insert discount code",
        shortcuts: [messageInputShortcuts.INSERT_DISCOUNT_CODE],
      },
      {
        description: "Open macros",
        shortcuts: [messageInputShortcuts.OPEN_MACRO_SELECTOR],
      },
    ],
  },
  {
    key: "actions",
    label: "Ticket actions",
    entries: [
      {
        description: "Assign",
        shortcuts: [supportTicketActionShortcuts.ASSIGN],
      },
      {
        description: "Manage tags",
        shortcuts: [supportTicketActionShortcuts.ADD_TAG],
      },
      {
        description: "Set priority",
        shortcuts: [supportTicketActionShortcuts.SET_PRIORITY],
      },
      {
        description: "Delete",
        shortcuts: [supportTicketActionShortcuts.DELETE],
      },
      { description: "Merge", shortcuts: [supportTicketActionShortcuts.MERGE] },
    ],
  },
  {
    key: "status",
    label: "Status",
    entries: [
      { description: "Close", shortcuts: [supportTicketActionShortcuts.CLOSE] },
      {
        description: "Reopen",
        shortcuts: [
          supportTicketActionShortcuts.REOPEN,
          supportTicketDetailShortcuts.REOPEN,
        ],
      },
      {
        description: "Mark as in progress",
        shortcuts: [
          supportTicketActionShortcuts.MARK_IN_PROGRESS,
          supportTicketDetailShortcuts.MARK_IN_PROGRESS,
        ],
      },
      {
        description: "Snooze",
        shortcuts: [supportTicketActionShortcuts.SNOOZE],
      },
      {
        description: "Mark as read",
        shortcuts: [supportTicketActionShortcuts.MARK_READ],
      },
      {
        description: "Mark as unread",
        shortcuts: [supportTicketActionShortcuts.MARK_UNREAD],
      },
      {
        description: "Archive",
        shortcuts: [supportTicketActionShortcuts.ARCHIVE],
      },
    ],
  },
  {
    key: "priority",
    label: "Priority",
    entries: [
      {
        description: "Set priority to urgent",
        shortcuts: [priorityShortcuts.PRIORITY_URGENT],
      },
      {
        description: "Clear priority",
        shortcuts: [priorityShortcuts.PRIORITY_CLEAR],
      },
    ],
  },
  {
    key: "snooze",
    label: "Snooze",
    entries: [
      { description: "Snooze 1 hour", shortcuts: [snoozeShortcuts.SNOOZE_1H] },
      { description: "Snooze 4 hours", shortcuts: [snoozeShortcuts.SNOOZE_4H] },
      {
        description: "Snooze 24 hours",
        shortcuts: [snoozeShortcuts.SNOOZE_24H],
      },
      {
        description: "Snooze until a custom time",
        shortcuts: [snoozeShortcuts.SNOOZE_CUSTOM],
      },
      {
        description: "Clear snooze",
        shortcuts: [snoozeShortcuts.CLEAR_SNOOZE],
      },
    ],
  },
  {
    key: "commandPalette",
    label: "Command palette",
    entries: [
      {
        description: "Open command palette",
        shortcuts: [commandPaletteShortcuts.TOGGLE],
      },
      { description: "Close", shortcuts: [commandPaletteShortcuts.CLOSE] },
      {
        description: "Move down",
        shortcuts: [commandPaletteShortcuts.NAVIGATE_DOWN],
      },
      {
        description: "Move up",
        shortcuts: [commandPaletteShortcuts.NAVIGATE_UP],
      },
      { description: "Open", shortcuts: [commandPaletteShortcuts.OPEN] },
      {
        description: "Open in new tab",
        shortcuts: [commandPaletteShortcuts.OPEN_NEW_TAB],
      },
      { description: "Go back", shortcuts: [commandPaletteShortcuts.GO_BACK] },
    ],
  },
];
